import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Redo2, RefreshCw, Undo2 } from "lucide-react";
import { type ReactNode, useEffect, useEffectEvent } from "react";

import { useBoxDraft } from "../lib/boxDraft";
import { useBoxDraftStore } from "../lib/boxDraftStore";
import {
  type BoxArtUpload,
  type BoxDuplicateResult,
  type BoxEditorData,
  type BoxMetaPatch,
  type BoxMetaResult,
  type BoxRefreshInput,
  type BoxWriteHandle,
  boxArtUrls,
  type PublishVersionInput,
  type PublishVersionResult,
} from "../lib/boxEditor";
import {
  BOX_EDITOR_TAB_LABELS,
  BOX_EDITOR_TABS,
  type BoxEditorSearch,
  resolveBoxEditorTab,
} from "../lib/boxEditorTabs";
import { useCurrency } from "../lib/currency";
import {
  type DropTableDraft,
  hasPublishPrice,
  publishBlockedReason,
  useDropTableDraft,
} from "../lib/dropTableDraft";
import { formatDate } from "../lib/format";
import type { OpenerStyleChoice } from "../lib/openerStyles";
import { useStandingNotice } from "../lib/useStandingNotice";
import { BoxActionsMenu } from "./BoxActionsMenu";
import { BoxArtCard } from "./BoxArtCard";
import { BoxMetadataCard } from "./BoxMetadataCard";
import { BoxNameHeading } from "./BoxNameHeading";
import { BoxStatusActions } from "./BoxStatusActions";
import { StatusBadge } from "./badges";
import { DropTableEditor } from "./DropTableEditor";
import { ErrorAlert } from "./ErrorAlert";
import { OpenerStyleSelect } from "./OpenerStyleSelect";

/** The opener styles a box may name, and what it opens with when it names none. */
export interface BoxOpenerStyleChoices {
  styles: readonly OpenerStyleChoice[];
  inheritLabel: string;
  inheritedStyleId: string | null;
}

/** An editor tab a page adds after the ones every box has. */
export interface BoxEditorTab {
  value: string;
  label: string;
  content: ReactNode;
}

/** The writes a box editor page binds to its router, each already addressed to one box. */
export interface BoxEditorWrites {
  update: BoxWriteHandle<BoxMetaPatch, BoxMetaResult>;
  publish: BoxWriteHandle<PublishVersionInput, PublishVersionResult>;
  /** Runs the processor over the published rows now, rather than at the next cron tick. */
  refresh: BoxWriteHandle<BoxRefreshInput>;
  /** Copies the box as a new draft; the page opens the copy. */
  duplicate: BoxWriteHandle<BoxRefreshInput, BoxDuplicateResult>;
  /** Deletes a box nothing references; the page leaves for the list. */
  remove: BoxWriteHandle<BoxRefreshInput>;
  /** Stores one image and answers its URL, which goes into the draft. */
  uploadArt: BoxArtUpload;
}

/**
 * The whole box editor, for both surfaces that have one: a tenant's own boxes
 * (`boxes.*`, env-scoped, /tenants/$tenantId/boxes/$boxId) and the box templates
 * (`platform.boxTemplates.*`, global, /templates/$boxId). Each page is left with only what
 * actually differs — its router, and what it adds through the props below.
 *
 * Nothing here knows a router: writes arrive as `BoxWriteHandle`s already bound to one box,
 * and the box itself as `BoxEditorData`.
 *
 * Every edit lands in the box's draft (`lib/boxDraftStore.ts`), which lives in this browser
 * until the header's Save or Publish writes it: a reload keeps it, undo walks it back, and
 * Discard throws it away. The tabs read that one draft, and the tab rides the URL (`?tab=`).
 */
export function BoxEditorLayout({
  box,
  openerStyles,
  isLoading,
  loadError,
  writes,
  isCatalog,
  parentCrumb,
  notice,
  archiveConfirm,
  readOnly,
  generatedTable,
  tabs = [],
}: {
  box: BoxEditorData | undefined;
  isLoading: boolean;
  loadError: string | null;
  writes: BoxEditorWrites;
  /** A box template. Named for the `box.isCatalog` field /v1 returns. */
  isCatalog: boolean;
  /**
   * The crumb before this box, e.g. `<Link to="/templates">Box templates</Link>`. The
   * box's own name is appended here; a page inside TenantLayout omits it, because
   * that header already owns the trail.
   */
  parentCrumb?: ReactNode;
  /** Shown under the header — what publishing a version will affect. */
  notice?: ReactNode;
  archiveConfirm?: { title: string; description: string };
  /** Viewer mode: everything renders, every write control is hidden or off. */
  readOnly?: boolean;
  /**
   * Set when the server generates the drop table (a Shopify rule box): the table, price and
   * weighting render read-only under this notice and the editor never publishes a version.
   * Name and art stay editable, since they are `boxes.update`.
   */
  generatedTable?: ReactNode;
  /** Appended after the built-in tabs, e.g. the box template's Tenants. */
  tabs?: BoxEditorTab[];
  /** The Details tab's opener style picker; absent while the styles load. */
  openerStyles?: BoxOpenerStyleChoices;
}) {
  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading box…</div>;
  }
  if (loadError) return <ErrorAlert message={loadError} />;
  if (!box) return null;

  return (
    <BoxEditorBody
      box={box}
      openerStyles={openerStyles}
      writes={writes}
      isCatalog={isCatalog}
      parentCrumb={parentCrumb}
      notice={notice}
      archiveConfirm={archiveConfirm}
      readOnly={readOnly}
      generatedTable={generatedTable}
      tabs={tabs}
    />
  );
}

/** Whether a key event is aimed at a field, whose own undo must keep working. */
function isTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLInputElement && target.type !== "checkbox")
  );
}

/** The header's primary button: what it says, and why it is off when it is. */
function saveButton(
  draft: DropTableDraft,
  metaChanged: boolean,
  tableChanged: boolean,
  stale: boolean,
  currentVersion: number,
  generated: boolean,
): { label: string; blocked?: string } {
  if (!tableChanged) {
    return metaChanged ? { label: "Save" } : { label: "Saved", blocked: "nothing to save" };
  }
  if (generated) return { label: "Publish", blocked: "this box's items come from its rules" };
  const blocked =
    publishBlockedReason(
      draft.priceMode,
      draft.sumOk,
      draft.weighting !== null,
      hasPublishPrice(draft.priceMode, draft.price),
    ) ??
    (draft.itemsOk ? undefined : "every row needs a catalog item or a listing") ??
    (draft.duplicateKeys.size === 0 ? undefined : "remove the rows marked Repeated");
  return { label: stale ? `Publish over v${currentVersion}` : "Publish", blocked };
}

function BoxEditorBody({
  box,
  openerStyles,
  writes,
  isCatalog,
  parentCrumb,
  notice,
  archiveConfirm,
  readOnly,
  generatedTable,
  tabs,
}: {
  box: BoxEditorData;
  writes: BoxEditorWrites;
  isCatalog: boolean;
  parentCrumb?: ReactNode;
  notice?: ReactNode;
  archiveConfirm?: { title: string; description: string };
  readOnly?: boolean;
  generatedTable?: ReactNode;
  tabs: BoxEditorTab[];
  openerStyles?: BoxOpenerStyleChoices;
}) {
  const currency = useCurrency();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as BoxEditorSearch;
  const tab = resolveBoxEditorTab(
    search.tab,
    tabs.map((extra) => extra.value),
  );
  const setTab = (value: string) => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        tab: value === "drop-table" ? undefined : value,
      }),
      replace: true,
    });
  };

  const boxDraft = useBoxDraft(box, currency);
  const draft = useDropTableDraft(boxDraft.state, boxDraft.edit, currency);
  const { metaChanged, tableChanged, stale } = boxDraft;
  // An archived box is history on the storefront; bring it back to draft to change it.
  const locked = !!readOnly || box.status === "archived";
  const busy = writes.update.isPending || writes.publish.isPending;
  const generated = generatedTable != null;
  const primary = saveButton(
    draft,
    metaChanged,
    tableChanged,
    stale,
    box.currentVersion,
    generated,
  );

  useStandingNotice(stale && !readOnly, {
    id: `box-draft-stale-${box.id}`,
    message: `This box moved to version ${box.currentVersion} after your draft was started on version ${boxDraft.baseVersion}. Publishing replaces what changed.`,
    action: { label: "Discard draft", onClick: boxDraft.discard },
  });

  /**
   * Save then publish, as two writes: the metadata first, then the table, so a refused table
   * still leaves the name saved and the table in the draft. The draft is dropped only when
   * nothing was edited while the writes were in flight; otherwise it is rebuilt on the new
   * version and the later edits stay.
   */
  const save = () => {
    const submitted = boxDraft.state;
    const settle = (version: number) => {
      const store = useBoxDraftStore.getState();
      if (store.drafts[box.id]?.present === submitted) store.discard(box.id);
      else store.rebase(box.id, version);
    };
    const publishTable = () => {
      if (generated || !draft.weighting) return;
      writes.publish.submit(
        {
          dropTable: draft.publishRows,
          weighting: draft.weighting,
          // WEIGHT_TARGET computes the price from the margin and refuses one sent with it.
          ...(draft.weighting.type === "WEIGHT_TARGET" ? {} : { priceCents: draft.price ?? 0 }),
          // A stale draft publishes over the newer version only because the button said so.
          expectedVersion: stale ? box.currentVersion : boxDraft.baseVersion,
        },
        { onSuccess: (result) => settle(result.box.currentVersion) },
      );
    };
    if (metaChanged) {
      writes.update.submit(
        {
          name: submitted.name.trim() || box.name,
          imageUrl: submitted.imageUrl,
          ...(submitted.art ?? boxArtUrls(box)),
        },
        {
          onSuccess: (saved) => (tableChanged ? publishTable() : settle(saved.currentVersion)),
        },
      );
    } else if (tableChanged) {
      publishTable();
    }
  };

  // The heading's rename saves the name alone; a draft that exists takes the new name too, so it
  // neither shows the old one nor counts the rename as an unsaved change.
  const rename = (name: string, done: () => void) => {
    writes.update.submit(
      { name },
      {
        onSuccess: () => {
          if (useBoxDraftStore.getState().drafts[box.id]) {
            boxDraft.edit((s) => ({ ...s, name }), "name");
          }
          toast.success(`Renamed to "${name}"`);
          done();
        },
      },
    );
  };

  const onKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (locked || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z") return;
    if (isTextTarget(event.target)) return;
    event.preventDefault();
    if (event.shiftKey) boxDraft.redo();
    else boxDraft.undo();
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => onKeyDown(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  const dirty = metaChanged || tableChanged;
  // "nothing to save" is the resting state, not a reason worth showing.
  const blockedReason =
    dirty && primary.blocked && primary.label !== "Saved"
      ? `Can't ${primary.label.toLowerCase()}: ${primary.blocked}`
      : undefined;

  return (
    <div className="flex flex-col gap-6">
      {parentCrumb && (
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
          {parentCrumb}
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">{box.name}</span>
        </nav>
      )}

      {/* The actions never wrap under the title on a wide screen: a status or a blocked reason
          appearing in the subtitle would otherwise push the whole page down. */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 md:flex-1">
          <BoxNameHeading
            name={boxDraft.state.name || box.name}
            editable={!locked}
            pending={writes.update.isPending}
            onRename={rename}
          />
          <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <StatusBadge status={box.status} />
            <span className="shrink-0 whitespace-nowrap">
              version {box.currentVersion || "unpublished"} · created {formatDate(box.createdAt)}
            </span>
            {/* A generated table is the server's to write, which the page should say before the
                disabled editor makes it look broken. */}
            {generated && (
              <Badge
                variant="secondary"
                className="shrink-0"
                title="The drop table is rebuilt automatically; see the Drop table tab"
              >
                <RefreshCw />
                Managed automatically
              </Badge>
            )}
            {dirty && !readOnly && (
              <Badge variant="secondary" className="shrink-0">
                Unsaved changes
              </Badge>
            )}
          </p>
          {/* A disabled button takes no hover, so its title alone never shows why. The line is
              always there, empty or not, so the reason coming and going never moves the page;
              a long one is cut short, with the whole of it on hover. */}
          {!readOnly && (
            <p className="mt-1 h-5 truncate text-sm text-destructive" title={blockedReason}>
              {blockedReason}
            </p>
          )}
        </div>
        {!readOnly && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={boxDraft.undo}
              disabled={locked || !boxDraft.canUndo}
              title="Undo (⌘Z)"
            >
              <Undo2 />
              <span className="sr-only">Undo</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={boxDraft.redo}
              disabled={locked || !boxDraft.canRedo}
              title="Redo (⇧⌘Z)"
            >
              <Redo2 />
              <span className="sr-only">Redo</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={boxDraft.discard}
              disabled={!dirty || busy}
              title="Throw the draft away and show the saved box"
            >
              Discard
            </Button>
            <Button
              type="button"
              onClick={save}
              disabled={locked || busy || primary.blocked !== undefined}
              title={primary.blocked}
              // Saved / Save / Saving… / Publish differ in width; the row around it should not move.
              className="min-w-24"
            >
              {busy ? "Saving…" : primary.label}
            </Button>
            <BoxStatusActions
              status={box.status}
              currentVersion={box.currentVersion}
              onSetStatus={(status) => writes.update.submit({ status })}
              pending={writes.update.isPending}
            />
            <BoxActionsMenu
              box={box}
              writes={writes}
              archiveConfirm={archiveConfirm}
              generated={generated}
            />
          </div>
        )}
      </div>

      {notice}
      {box.status === "archived" && !readOnly && (
        <p className="text-sm text-muted-foreground">
          This box is archived, so it is read-only. Restore it to draft to edit it.
        </p>
      )}

      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <TabsList>
          {BOX_EDITOR_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {BOX_EDITOR_TAB_LABELS[value]}
            </TabsTrigger>
          ))}
          {tabs.map((extra) => (
            <TabsTrigger key={extra.value} value={extra.value}>
              {extra.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="drop-table" className="flex flex-col gap-6">
          {generatedTable}
          <DropTableEditor draft={draft} readOnly={locked || generated} sharedOnly={isCatalog} />
        </TabsContent>

        <TabsContent value="details">
          <BoxMetadataCard
            name={boxDraft.state.name}
            slug={box.slug}
            goLiveAt={box.goLiveAt ?? null}
            featured={isCatalog ? undefined : box.featured}
            onNameChange={(name) => boxDraft.edit((s) => ({ ...s, name }), "name")}
            onGoLiveAtChange={(goLiveAt) =>
              writes.update.submit(
                { goLiveAt },
                {
                  onSuccess: () =>
                    toast.success(
                      goLiveAt === null ? "Go-live date cleared." : "Go-live date saved.",
                    ),
                },
              )
            }
            // Template rows are one shared record across tenants, so featuring one would feature
            // it on every storefront that enabled it; the switch is tenant boxes only.
            onFeaturedChange={
              isCatalog || readOnly ? undefined : (featured) => writes.update.submit({ featured })
            }
            pending={writes.update.isPending}
            readOnly={locked}
          />
          {openerStyles && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Opener</CardTitle>
              </CardHeader>
              <CardContent>
                <OpenerStyleSelect
                  id="box-opener-style"
                  label="Opener style"
                  description="The animation this box opens with, themed by wherever it is shown. Saves as soon as it is picked."
                  styles={openerStyles.styles}
                  value={box.openerStyleId}
                  inheritLabel={openerStyles.inheritLabel}
                  inheritedStyleId={openerStyles.inheritedStyleId}
                  disabled={readOnly || writes.update.isPending}
                  onChange={(openerStyleId) =>
                    writes.update.submit(
                      { openerStyleId },
                      { onSuccess: () => toast.success("Opener style saved.") },
                    )
                  }
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="art">
          <BoxArtCard
            imageUrl={boxDraft.state.imageUrl}
            // A draft persisted before the art slots existed has none: the stored box's stand in.
            art={boxDraft.state.art ?? boxArtUrls(box)}
            onChange={(slot, url) =>
              boxDraft.edit(
                (s) =>
                  slot === "imageUrl"
                    ? { ...s, imageUrl: url }
                    : { ...s, art: { ...(s.art ?? boxArtUrls(box)), [slot]: url } },
                `art:${slot}`,
              )
            }
            upload={writes.uploadArt}
            readOnly={locked}
          />
        </TabsContent>

        {tabs.map((extra) => (
          <TabsContent key={extra.value} value={extra.value}>
            {extra.content}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
